import type { LeadFormState } from '@core/Atoms/Pdp/FormState.atom';
import type { SearchParamsType } from '@core/Atoms/SearchParams/SearchParams.atom';
import type { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import type { AllExperiments } from '@growthBookExperimentation/Entities/Experiments.entities';
import type { FlagKey } from '@growthBookExperimentation/Entities/FlagKeys.entities';
import type { TrackingDataType } from '@tracking/Entities/TrackingDataType.entity';
import { callStartToEvent } from '@tracking/Schemas/CallStart/CallStartToEvent';
import { callSubmitToEvent } from '@tracking/Schemas/CallSubmit/CallSubmitToEvent';
import { leadStartToEvent } from '@tracking/Schemas/Lead/LeadStart/LeadStartToEvent';
import { leadSubmitToEvent } from '@tracking/Schemas/Lead/LeadSubmit/LeadSubmitToEvent';
import { leadToEvent } from '@tracking/Schemas/Lead/LeadToEvent';
import { vehicleToListingEvent } from '@tracking/Schemas/Listing/VehicleToListingEvent';
import { vehicleToListingFeatureEvent } from '@tracking/Schemas/ListingFeature/VehicleToListingFeatureEvent';
import { marketingToEvent } from '@tracking/Schemas/Marketing/MarketingToEvent';
import { userContextToEvent } from '@tracking/Schemas/UserContext/UserContextToEvent';
import { getExperimentViewedEvents } from '@tracking/Utils/GetExperimentViewedEvents';

import type { LeadFormData } from '../Components/LeadFormData/FormConfig.entity';

interface Props {
  searchParams: SearchParamsType;
  userId: string | null | undefined;
  formState: LeadFormState;
  leadId: string;
  leadFormId: string;
  listingId: string | null;
  captiveId?: string;
  source?: string;
  vehicle: Vehicle;
  experiments?: AllExperiments;
  ctaLocation: string;
  leadAction: string;
  numSimilarVehicles?: number;
  financeQuoteChecked?: LeadFormData['financeQuote']['financeQuoteChecked'];
}

export function generateLeadTrackingData({
  searchParams,
  userId,
  formState,
  leadId,
  leadFormId,
  listingId,
  captiveId,
  source,
  vehicle,
  experiments,
  ctaLocation,
  leadAction,
  numSimilarVehicles,
  financeQuoteChecked = false,
}: Props): Record<string, TrackingDataType> {
  const marketingDataObj = {
    marketingValues: { ...(searchParams ?? {}) },
  };
  const userData = {
    userId,
    values: { ...formState },
  };
  let extraData = {};

  if (leadAction === 'submit') {
    const keys = Object.keys(formState);
    const hasOneClickDriftrock = keys.includes('oneClickDriftrockChkBox');
    const hasAnyDriftrock = hasOneClickDriftrock || keys.includes('driftrockChkBox');
    const isAnyDriftrockChecked = formState.oneClickDriftrockChkBox || formState.driftrockChkBox;
    //formState cant be relied on alone to check for oneclick, because we don't have similar vehicles at the time the state is initialized
    const hasOneClick = keys.includes('oneClickChkBox') && Boolean(numSimilarVehicles);

    /* eslint-disable @typescript-eslint/naming-convention */
    extraData = {
      has_oneClick_chkBox: hasOneClick,
      is_oneclick_chkBox_checked: formState.oneClickChkBox,
      num_similarVehicles: numSimilarVehicles,
      has_driftrock_chkBox: hasAnyDriftrock,
      is_driftrock_chkBox_checked: isAnyDriftrockChecked,
      financeQuoteChecked,
    };
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  const leadSubmitEventObj = {
    listingId: listingId as string,
    leadId,
    leadType: leadFormId as string,
    captiveId: captiveId as string,
    financeQuoteChecked,
  };
  const leadStartObj = {
    listingId,
    leadType: leadFormId as string,
    ctaLocation,
    financeQuoteChecked,
  };

  const leadEventObj = {
    vehicle,
    values: { ...formState },
    leadType: leadFormId as string,
    leadId,
    source,
    financeQuoteChecked,
  };
  // event actions
  const leadSubmitEvent = leadSubmitToEvent(leadSubmitEventObj);
  const leadStartEvent = leadStartToEvent(leadStartObj);
  const callStartEvent = callStartToEvent(leadStartObj);

  // event entities
  const userEvent = userContextToEvent(userData);
  const listingEvent = vehicleToListingEvent(vehicle);
  const marketingEvent = marketingToEvent(marketingDataObj);
  const listingFeatureEvent = vehicleToListingFeatureEvent(vehicle, extraData);
  const leadEvent = leadToEvent(leadEventObj);

  const selectedLeadFormExperimentIds: FlagKey[] = [
    'message-field-leadform-experiment',
    'uk-enrichment-leadform-experiment',
    'name-field-leadform-experiment',
    'dealername-leadform-experiment',
  ];

  const selectedExperimentViewedEvents = getExperimentViewedEvents(
    selectedLeadFormExperimentIds,
    experiments,
  );

  const contextStart = [
    listingEvent,
    listingFeatureEvent,
    userEvent,
    marketingEvent,

    ...((leadFormId === 'message-dealer' || leadFormId === 'request-callback') &&
    selectedExperimentViewedEvents.length > 0
      ? [...selectedExperimentViewedEvents]
      : []),
  ];

  const contextSubmit = [leadEvent, listingEvent, listingFeatureEvent, userEvent, marketingEvent];
  return {
    leadStart: { event: leadStartEvent, context: contextStart },
    leadSubmit: { event: leadSubmitEvent, context: contextSubmit },
    callStart: { event: callStartEvent, context: contextStart },
    callSubmit: { context: contextSubmit } as any,
  };
}

export const createEventWithCallNumber = (
  vehicleId: string,
  callNumber: string,
  ctaLocation: string,
) => {
  const leadSubmitEventObj = {
    listingId: vehicleId,
    leadId: '',
    leadType: 'call-dealer',
    callNumber: callNumber,
    ctaLocation: ctaLocation,
  };
  const callSubmitEvent = callSubmitToEvent(leadSubmitEventObj);
  return callSubmitEvent;
};
